import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { App, Button, Drawer, Form, Input, InputNumber, Select, Space, Table, Tag, Typography, } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiError } from '@/lib/api';
import { formatRupiah } from '@/lib/format';
import { useAuthStore } from '@/lib/auth-store';
const STATUS_COLORS = {
    AVAILABLE: 'green',
    RENTED: 'orange',
    OCCUPIED: 'purple',
};
export function NichesPage() {
    const { message } = App.useApp();
    const qc = useQueryClient();
    const hasPerm = useAuthStore((s) => s.hasPerm);
    const [roomFilter, setRoomFilter] = useState();
    const [statusFilter, setStatusFilter] = useState();
    const [drawer, setDrawer] = useState({ open: false });
    const [form] = Form.useForm();
    const q = useQuery({
        queryKey: ['abu-niches', roomFilter, statusFilter],
        queryFn: async () => (await api.get('/abu/niches', {
            params: { room_code: roomFilter, status: statusFilter },
        })).data,
    });
    const rooms = useMemo(() => {
        const s = new Set();
        q.data?.forEach((n) => s.add(n.room_code));
        return Array.from(s).sort();
    }, [q.data]);
    const summary = useMemo(() => {
        const s = { AVAILABLE: 0, RENTED: 0, OCCUPIED: 0 };
        q.data?.forEach((n) => { s[n.status] = (s[n.status] ?? 0) + 1; });
        return s;
    }, [q.data]);
    const createMut = useMutation({
        mutationFn: async (v) => (await api.post('/abu/niches', v)).data,
        onSuccess: () => {
            message.success('Lokus ditambahkan');
            qc.invalidateQueries({ queryKey: ['abu-niches'] });
            setDrawer({ open: false });
        },
        onError: (e) => message.error(apiError(e)),
    });
    const patchMut = useMutation({
        mutationFn: async ({ id, body }) => (await api.patch(`/abu/niches/${id}`, body)).data,
        onSuccess: () => {
            message.success('Lokus diperbarui');
            qc.invalidateQueries({ queryKey: ['abu-niches'] });
            setDrawer({ open: false });
        },
        onError: (e) => message.error(apiError(e)),
    });
    const cols = [
        { title: 'Ruang', dataIndex: 'room_code', width: 100 },
        { title: 'Lokus', dataIndex: 'niche_code', width: 120 },
        { title: 'Tier', dataIndex: 'tier', width: 100 },
        {
            title: 'Status', dataIndex: 'status', width: 120,
            render: (s) => _jsx(Tag, { color: STATUS_COLORS[s], children: s }),
        },
        {
            title: 'Harga/Th', dataIndex: 'price_year', width: 140, align: 'right',
            render: (v) => v ? _jsx("span", { className: "money", children: formatRupiah(v) }) : '—',
        },
        {
            title: '5 Th', dataIndex: 'price_pack_5y', width: 140, align: 'right',
            render: (v) => v ? _jsx("span", { className: "money", children: formatRupiah(v) }) : '—',
        },
        {
            title: '10 Th', dataIndex: 'price_pack_10y', width: 140, align: 'right',
            render: (v) => v ? _jsx("span", { className: "money", children: formatRupiah(v) }) : '—',
        },
        {
            title: '20 Th', dataIndex: 'price_pack_20y', width: 140, align: 'right',
            render: (v) => v ? _jsx("span", { className: "money", children: formatRupiah(v) }) : '—',
        },
        {
            title: 'Aksi', width: 90,
            render: (_, r) => hasPerm('abu.niche:edit') && (_jsx(Button, { size: "small", icon: _jsx(EditOutlined, {}), onClick: () => { setDrawer({ open: true, row: r }); form.setFieldsValue(r); } })),
        },
    ];
    return (_jsxs("div", { children: [_jsxs(Space, { style: { marginBottom: 16, display: 'flex', justifyContent: 'space-between', width: '100%' }, children: [_jsx(Typography.Title, { level: 3, style: { margin: 0 }, children: "Lokus Rumah Abu" }), hasPerm('abu.niche:create') && (_jsx(Button, { type: "primary", icon: _jsx(PlusOutlined, {}), onClick: () => { form.resetFields(); setDrawer({ open: true }); }, children: "Tambah Lokus" }))] }), _jsxs(Space, { style: { marginBottom: 16 }, wrap: true, children: [_jsx(Select, { allowClear: true, style: { width: 160 }, placeholder: "Filter ruang", value: roomFilter, onChange: (v) => setRoomFilter(v), options: rooms.map((r) => ({ value: r, label: r })) }), _jsx(Select, { allowClear: true, style: { width: 160 }, placeholder: "Filter status", value: statusFilter, onChange: (v) => setStatusFilter(v), options: Object.keys(STATUS_COLORS).map((s) => ({ value: s, label: s })) }), Object.entries(summary).map(([k, v]) => (_jsxs(Tag, { color: STATUS_COLORS[k], children: [k, ": ", v] }, k)))] }), _jsx(Table, { rowKey: "id", loading: q.isLoading, dataSource: q.data ?? [], columns: cols, size: "middle", pagination: { pageSize: 30 } }), _jsx(Drawer, { open: drawer.open, onClose: () => setDrawer({ open: false }), title: drawer.row ? `Edit ${drawer.row.room_code}·${drawer.row.niche_code}` : 'Tambah Lokus', width: 480, destroyOnClose: true, extra: _jsx(Button, { type: "primary", loading: createMut.isPending || patchMut.isPending, onClick: () => form.validateFields().then((v) => {
                        const payload = {
                            ...v,
                            price_year: v.price_year?.toString(),
                            price_pack_5y: v.price_pack_5y?.toString(),
                            price_pack_10y: v.price_pack_10y?.toString(),
                            price_pack_20y: v.price_pack_20y?.toString(),
                        };
                        if (drawer.row)
                            patchMut.mutate({ id: drawer.row.id, body: payload });
                        else
                            createMut.mutate(payload);
                    }), children: "Simpan" }), children: _jsxs(Form, { form: form, layout: "vertical", preserve: false, children: [_jsx(Form.Item, { name: "room_code", label: "Kode Ruang", rules: [{ required: true }], children: _jsx(Input, { disabled: !!drawer.row, placeholder: "mis. R1" }) }), _jsx(Form.Item, { name: "niche_code", label: "Kode Lokus", rules: [{ required: true }], children: _jsx(Input, { disabled: !!drawer.row, placeholder: "mis. A-042" }) }), _jsx(Form.Item, { name: "tier", label: "Tier", children: _jsx(Select, { allowClear: true, options: [
                                    { value: 'premium', label: 'Premium' },
                                    { value: 'standard', label: 'Standard' },
                                    { value: 'economy', label: 'Economy' },
                                ] }) }), [
                            { name: 'price_year', label: 'Harga per Tahun' },
                            { name: 'price_pack_5y', label: 'Paket 5 Tahun' },
                            { name: 'price_pack_10y', label: 'Paket 10 Tahun' },
                            { name: 'price_pack_20y', label: 'Paket 20 Tahun' },
                        ].map((f) => (_jsx(Form.Item, { name: f.name, label: f.label, children: _jsx(InputNumber, { style: { width: '100%' }, min: 0, formatter: (v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.'), parser: ((v) => parseFloat((v ?? '').replace(/\./g, '')) || 0) }) }, f.name))), drawer.row && (_jsx(Form.Item, { name: "status", label: "Status", children: _jsx(Select, { options: Object.keys(STATUS_COLORS).map((s) => ({ value: s, label: s })) }) })), _jsx(Form.Item, { name: "notes", label: "Catatan", children: _jsx(Input.TextArea, { rows: 2 }) })] }) })] }));
}
